/**
 * client/src/components/providers/org-context-provider.tsx
 *
 * React Context provider for the personal/org context model (§2.4).
 *
 * Memberships are fetched via TanStack Query, gated on the cached
 * `["auth/me"]` query so the request only fires once auth is known.
 * This closes two failure modes seen on first sign-in (issue #503):
 *   1. A mount-time fetch could race the auth cookie set and 401 silently.
 *   2. Once `user` flipped null → object elsewhere in the tree, the
 *      provider never re-fetched, leaving the OrgSwitcher empty until
 *      a full reload.
 *
 * `switchToOrg` / `switchToPersonal` update local state optimistically, then
 * POST to `/api/user/org-switch` for server-side audit logging in the
 * background — a rejected POST rolls the optimistic state back and rethrows
 * (console transition tab lag, #1965).
 * `useOrgContext()` throws if used outside the provider (fail-loud).
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { OrgMemberRole } from "@shared/org-types";
import { ApiError } from "@/lib/queryClient";
import { fetchAuthMe, AUTH_ME_QUERY_KEY } from "@/lib/auth-me";
import type { CurrentUser } from "@/App";

export interface OrgMembershipSummary {
  membershipId: string;
  orgId: string;
  orgName: string;
  orgDomain: string;
  connectionType: "member" | "admin" | "connected";
  role: OrgMemberRole;
  vettedAt: string | null;
  acceptedAt: string | null;
  nodeKind: "personal" | "org";
  // onboarding-identity-binding spec Module 01 §4 (AC-11): lets the setup
  // wizard detect "administrator of an org founded ≤24h ago" off this
  // already-fetched list — no new endpoint. ISO-8601; null only if an older
  // cached response predates this field.
  orgCreatedAt: string | null;
}

export interface OrgContextState {
  currentContext: "personal" | "org";
  /**
   * The seat as the SERVER names it — `"personal"` (the one literal
   * `resolveIssuingOrg` translates) or the active org's node id. Derived here so
   * the `?? "personal"` translation of a client-side null into a server contract
   * exists in exactly one place; consumers destructure it rather than
   * re-deriving it per surface (org/personal separation, #2459).
   */
  actingContext: string;
  activeOrgId: string | null;
  activeOrgName: string | null;
  isOrgContext: boolean;
  isPlatformAdmin: boolean;
  /**
   * The signed-in person's own username, so the "Acting as" surfaces can name
   * WHOSE personal profile this is. Null until auth/me resolves.
   */
  username: string | null;
  memberships: OrgMembershipSummary[];
  isLoading: boolean;
  /**
   * The identity/membership reads FAILED. Distinct from `isLoading` and from a
   * genuinely empty `memberships`: on failure `memberships` is `[]` and
   * `isLoading` is false, which is byte-identical to "this person belongs to
   * nothing" — so any consumer that derives a RELATIONSHIP from memberships
   * (e.g. which side of a challenge the reader is on) would state a falsehood
   * about them rather than admit it does not know. Consumers that only need a
   * list can keep ignoring this.
   */
  isError: boolean;
  switchToOrg: (orgId: string, orgName: string) => Promise<void>;
  switchToPersonal: () => Promise<void>;
  refresh: () => Promise<void>;
}

interface OrgMembershipsResponse {
  memberships: OrgMembershipSummary[];
}

const OrgContext = createContext<OrgContextState | null>(null);

export function OrgContextProvider({ children }: { children: ReactNode }) {
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [activeOrgName, setActiveOrgName] = useState<string | null>(null);

  // Subscribe to the cached auth/me query so memberships fetch only fires
  // once auth is known. `App.tsx` populates this same key — TanStack
  // dedupes by queryKey so this does not duplicate the request.
  const userQuery = useQuery<CurrentUser | null>({
    queryKey:  AUTH_ME_QUERY_KEY,
    queryFn:   fetchAuthMe,
    staleTime: 30_000,
    // No `retry: false` — inherit the resilient global policy so a hung/failed
    // bootstrap fetch recovers instead of stranding the provider in isLoading
    // forever (fetchAuthMe carries the timeout + 401/403 → null short-circuit).
  });
  const user = userQuery.data ?? null;

  const queryClient = useQueryClient();
  const membershipsKey = useMemo(
    () => ["org-memberships", user?.id ?? null] as const,
    [user?.id],
  );

  const membershipsQuery = useQuery<OrgMembershipSummary[]>({
    queryKey: membershipsKey,
    queryFn:  async () => {
      const res = await fetch("/api/user/org-memberships", { credentials: "include" });
      if (!res.ok) {
        throw new ApiError(res.status, res.statusText);
      }
      const body = (await res.json()) as OrgMembershipsResponse;
      return body.memberships ?? [];
    },
    enabled: !!user,
  });

  const memberships = membershipsQuery.data ?? [];
  const isLoading = userQuery.isLoading || (!!user && membershipsQuery.isLoading);
  // A STALE-BUT-PRESENT user is good enough to derive a relationship from, so a
  // failing auth/me REFETCH must not count as an error here. TanStack keeps
  // `data` across a failed refetch, and auth/me is 30s-stale — so ORing
  // `userQuery.isError` unconditionally meant a sustained 5xx on that
  // background refetch flipped a signed-in reader into "we don't know who you
  // are" mid-flow, taking their actions with them, even though the memberships
  // this state is about were loaded and fine. The `!user` arm is kept for
  // symmetry only: with no user at all, `guards.tsx` has already redirected to
  // /auth before any consumer of this reads it.
  const isError = (userQuery.isError && !user) || (!!user && membershipsQuery.isError);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: membershipsKey });
  }, [queryClient, membershipsKey]);

  // Both mutators update activeOrgId/activeOrgName OPTIMISTICALLY, before the
  // audit-log POST resolves: currentContext (and every nav array derived
  // from it in sidebar.tsx/bottom-tabs.tsx) is a synchronous function of
  // this state, so awaiting the network round trip first left the OLD
  // context's tabs on screen for the duration of the request (console
  // transition tab lag, #1965). The POST still runs — and a rejection
  // (e.g. a stale membership list, 403 not_member_of_target_org) still rolls
  // the optimistic state back and rethrows, so callers that depend on the
  // switch actually landing (StepFoundOrg.tsx, SigningKeyRequiredNotice.tsx)
  // keep their existing await-then-proceed contract.
  //
  // `switchGenerationRef` guards that rollback against a rapid second switch:
  // without it, an earlier switch's rejection could reset state to ITS
  // prior value after a later switch has already moved state on, silently
  // clobbering the newer (still in-flight or already-succeeded) context.
  // Only the switch that is still the LATEST one in flight is allowed to
  // roll back.
  const switchGenerationRef = useRef(0);
  // Mirrors activeOrgId/activeOrgName so the rollback below can read the
  // "previous" value without closing over React state — that keeps
  // `performSwitch` (and switchToOrg/switchToPersonal, which just forward to
  // it) at stable `[]` deps instead of re-creating on every switch (simplify
  // pass, #1965).
  const activeOrgRef = useRef<{ id: string | null; name: string | null }>({
    id:   null,
    name: null,
  });

  // switchToOrg and switchToPersonal are the same ceremony (snapshot prev →
  // bump generation → set optimistically → POST → roll back on reject) with
  // only the target org differing — shared here so that ceremony exists in
  // exactly one place (simplify pass, #1965).
  const performSwitch = useCallback(
    async (orgId: string | null, orgName: string | null) => {
      const prev = activeOrgRef.current;
      const generation = ++switchGenerationRef.current;
      activeOrgRef.current = { id: orgId, name: orgName };
      setActiveOrgId(orgId);
      setActiveOrgName(orgName);
      try {
        const res = await fetch("/api/user/org-switch", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ toOrgId: orgId }),
        });
        if (!res.ok) throw new Error("switch_failed");
      } catch (err) {
        if (switchGenerationRef.current === generation) {
          activeOrgRef.current = prev;
          setActiveOrgId(prev.id);
          setActiveOrgName(prev.name);
        }
        throw err;
      }
    },
    [],
  );

  const switchToOrg = useCallback(
    (orgId: string, orgName: string) => performSwitch(orgId, orgName),
    [performSwitch],
  );

  const switchToPersonal = useCallback(
    () => performSwitch(null, null),
    [performSwitch],
  );

  const value = useMemo<OrgContextState>(
    () => ({
      currentContext: activeOrgId ? "org" : "personal",
      actingContext: activeOrgId ?? "personal",
      activeOrgId,
      activeOrgName,
      isOrgContext: activeOrgId !== null,
      isPlatformAdmin: user?.role === "admin-platform",
      username: user?.username ?? null,
      memberships,
      isLoading,
      isError,
      switchToOrg,
      switchToPersonal,
      refresh,
    }),
    [
      user,
      activeOrgId,
      activeOrgName,
      memberships,
      isLoading,
      isError,
      switchToOrg,
      switchToPersonal,
      refresh,
    ],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrgContext(): OrgContextState {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error("useOrgContext must be used inside OrgContextProvider");
  }
  return ctx;
}

/**
 * The same context, `null` when there is no provider, for readers that must
 * DEGRADE rather than crash.
 *
 * The throwing variant above is right for a surface that cannot function
 * without a context — failing loud beats rendering a lie. It is wrong for a
 * surface whose honest answer to "no context" is simply "then I don't know
 * who you are", because App.tsx mounts the whole route tree WITHOUT this
 * provider for `user.guardianOnly` (see the ternary at the end of the router):
 * a throw there is a white screen, and a white screen tells the reader less
 * than a read-only page does.
 *
 * App.tsx mounts the route tree WITHOUT this provider for `user.guardianOnly`.
 * The guardian-only route guard should keep such a reader off the surfaces that
 * use this hook, so `null` is DEFENSIVE rather than a path known to be
 * reachable — but "defensive" is worth having here, because the failure mode of
 * the throwing variant is a white screen.
 *
 * Use this ONLY where the caller has a defined, fail-closed behaviour for
 * `null` — never as a way to skip wiring the provider up.
 */
export function useOptionalOrgContext(): OrgContextState | null {
  return useContext(OrgContext);
}
